"use client"
import styled from "@emotion/styled"
import { useInView } from "react-intersection-observer"
import "swiper/css"
import "swiper/css/pagination"
import { Swiper, SwiperSlide } from "swiper/react"
import { Desktop, Mobile, transitionDelayTime, TransitionUp, useInViewConfig } from "ui"
import ImageWithAlt from "ui/ImageWithAlt"
import { getUmbracoMediaUrl } from "utils"
import { RentProjectVisualization } from "utils/projects"
import ProjectVisualizationItem from "./ProjectVisualizationItem"

type Props = {
  data: RentProjectVisualization
  locale: string
}

const StyledContainer = styled.section<{ locale: string }>`
  z-index: 0;
  position: relative;
  width: 100%;
  aspect-ratio: 375/728;
  padding-top: 115px;
  display: flex;
  flex-direction: column;
  align-items: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    aspect-ratio: 1736/1201;
    padding-top: 180px;
  }
`
const StyledImageWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  display: flex;
  z-index: -1;
`

const StyledImage = styled(ImageWithAlt)`
  object-fit: cover;
  object-position: top center;
  ${(props) => props.theme.breakpoints.up("md")} {
    object-position: bottom center;
  }
`

const StyledHeading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  color: white;
  gap: 8px;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 16px;
  }
`

const StyledHeadingH2 = styled.h2`
  font-size: 40px;
  font-weight: 700;
  line-height: 150%;
  letter-spacing: 0.4px;
  text-align: center;
  margin: 0;
  display: flex;
  width: max-content;
  flex-direction: column;
  &::after {
    content: "";
    background-image: url("/icons/4rent-heading-underline-mobile.svg");
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
    display: flex;
    width: 100%;
    height: 4px;
    position: relative;
    top: -4px;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px; /* 90px */
    letter-spacing: 0.6px;
    &::after {
      background-image: url("/icons/4rent-heading-underline-desktop.svg");
    }
  }
`

const StyledHeadingP = styled.p`
  font-size: 18px;
  font-weight: 300;
  line-height: 150%; /* 27px */
  letter-spacing: -0.36px;
  margin: 0;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 20px;
    letter-spacing: -0.4px;
  }
`

const StyledItemsWrapper = styled.div`
  position: absolute;
  bottom: 24px;
  inset-inline-start: 0;

  display: flex;
  justify-content: center;
  overflow: hidden;
  width: 100%;

  z-index: 1;

  > div {
    display: flex;
    max-width: 1020px;
    padding-inline: 20px;
    width: 100%;
  }

  ${(props) => props.theme.breakpoints.up("lg")} {
    bottom: 48px;
  }
`

const StyledSwiper = styled(Swiper)`
  overflow: visible !important;
  cursor: grab !important;
  &:active {
    cursor: grabbing !important;
  }
`

const ProjectVisualization = ({ data, locale }: Props) => {
  const { ref, inView } = useInView(useInViewConfig)
  const desktopImage = data.imageForDesktop?.[0]
  if (desktopImage?.url) {
    desktopImage.url = getUmbracoMediaUrl(desktopImage.url)
  }
  const mobileImage = data.imageForMobile?.[0] ?? desktopImage
  if (mobileImage?.url) {
    mobileImage.url = getUmbracoMediaUrl(mobileImage.url)
  }

  const title = data?.title
  const subTitle = data?.subTitle
  const anchorId = data?.anchorId

  const items = data?.items || []

  const shouldRender = items.length > 0 && (desktopImage || mobileImage)
  if (!shouldRender) {
    return null
  }
  return (
    <TransitionUp ref={ref} $inView={inView} $delay={transitionDelayTime}>
      <StyledContainer locale={locale} {...(anchorId ? { id: anchorId } : {})}>
        <StyledImageWrapper>
          <Desktop sx={{ position: "relative", width: "100%", height: "100%" }}>
            <StyledImage style={{ objectFit: "cover" }} image={desktopImage} fill />
          </Desktop>
          <Mobile sx={{ position: "relative", width: "100%", height: "100%" }}>
            <ImageWithAlt style={{ objectFit: "cover" }} image={mobileImage} fill />
          </Mobile>
        </StyledImageWrapper>
        <StyledHeading>
          {title && <StyledHeadingH2>{title}</StyledHeadingH2>}
          {subTitle && <StyledHeadingP>{subTitle}</StyledHeadingP>}
        </StyledHeading>
        <StyledItemsWrapper>
          <div>
            <StyledSwiper
              dir={locale === "he" ? "rtl" : "ltr"}
              slidesPerView={1.5}
              spaceBetween={16}
              grabCursor={true}
              breakpoints={{
                768: {
                  spaceBetween: 24,
                  slidesPerView: 2.5,
                },
                1366: {
                  spaceBetween: 40,
                  slidesPerView: 4,
                },
              }}
              // style={{
              //   width: "100%",
              //   height: "100%",
              // }}
            >
              {items?.map((item, index) => (
                <SwiperSlide key={index}>
                  <ProjectVisualizationItem
                    title={item.title}
                    text={item.text}
                    button={item.button}
                    buildingName={item.buildingName}
                  />
                </SwiperSlide>
              ))}
            </StyledSwiper>
          </div>
        </StyledItemsWrapper>
      </StyledContainer>
    </TransitionUp>
  )
}
export default ProjectVisualization
